import { useCallback, useEffect, useRef, useState } from "react";
import { listDeployServers, deployPlugin, type DeployServer, type DeployResult } from "../api/deploy";
import { HttpError } from "../api/errors";

type Props = { project: string; defaultBranch?: string };
type State = "idle" | "picking" | "building" | "done" | "error";

function ServerIcon() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="2" y="3" width="20" height="8" rx="2" />
      <rect x="2" y="13" width="20" height="8" rx="2" />
      <path d="M6 7h.01M6 17h.01" />
    </svg>
  );
}

export default function DeployButton({ project, defaultBranch }: Props) {
  const [state, setState] = useState<State>("idle");
  const [servers, setServers] = useState<DeployServer[]>([]);
  const [serversLoaded, setServersLoaded] = useState(false);
  const [server, setServer] = useState<string>("");
  const [branch, setBranch] = useState<string>(defaultBranch ?? "");
  const [message, setMessage] = useState<string>("");
  const [detail, setDetail] = useState<string>("");
  const resetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (resetTimer.current) clearTimeout(resetTimer.current); }, []);

  const loadServers = useCallback(async () => {
    if (serversLoaded) return;
    try {
      const s = await listDeployServers();
      setServers(s);
      setServer((cur) => cur || s[0]?.id || "");
      setServersLoaded(true);
    } catch {
      // surfaced when the user tries to deploy
    }
  }, [serversLoaded]);

  const openPicker = useCallback(() => {
    setState((s) => (s === "picking" ? "idle" : "picking"));
    void loadServers();
  }, [loadServers]);

  const scheduleReset = useCallback(() => {
    if (resetTimer.current) clearTimeout(resetTimer.current);
    resetTimer.current = setTimeout(() => setState("idle"), 8000);
  }, []);

  const run = useCallback(async () => {
    if (!server) { setMessage("Pick a target server first."); setState("error"); return; }
    setMessage(""); setDetail(""); setState("building");
    try {
      const res: DeployResult = await deployPlugin(project, branch, server);
      setMessage(`Deployed ${res.jar} → ${res.serverLabel}`);
      setDetail(`commit ${res.commit} · ${res.remotePath} · restart the server to load it`);
      setState("done");
      scheduleReset();
    } catch (e) {
      const msg = e instanceof HttpError
        ? e.status === 403 ? "You do not have access to deploy this project."
          : e.status === 401 ? "Your session expired — please sign in again." : e.message
        : (e as Error).message;
      setMessage(msg); setState("error");
    }
  }, [project, branch, server, scheduleReset]);

  const busy = state === "building";

  return (
    <div className="deploy-wrap">
      <button
        type="button"
        className={`deploy-btn${busy ? " deploy-btn-busy" : ""}`}
        disabled={busy}
        aria-busy={busy}
        onClick={openPicker}
        title="Build this branch and upload the plugin jar to a server"
      >
        {busy ? <span className="deploy-spinner" aria-hidden="true" /> : <ServerIcon />}
        {busy ? "Building…" : "Deploy to Server"}
      </button>

      {state === "picking" && (
        <div className="deploy-popover" role="dialog" aria-label="Deploy to Server">
          <label className="deploy-field">
            <span>Server</span>
            <select value={server} onChange={(e) => setServer(e.target.value)} autoFocus>
              {servers.length === 0 && <option value="">No servers configured</option>}
              {servers.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </label>
          <label className="deploy-field">
            <span>Branch</span>
            <input type="text" value={branch} placeholder="default branch" spellCheck={false}
              onChange={(e) => setBranch(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") void run(); if (e.key === "Escape") setState("idle"); }} />
          </label>
          <div className="deploy-actions">
            <button type="button" className="deploy-btn deploy-btn-primary" onClick={() => void run()}>
              <ServerIcon /> Deploy
            </button>
            <button type="button" className="deploy-cancel" onClick={() => setState("idle")}>Cancel</button>
          </div>
          <p className="deploy-hint">Builds the selected branch and uploads the jar into the server's plugins folder. Restart the server to load it.</p>
        </div>
      )}

      {state === "done" && (
        <div className="deploy-popover deploy-result-ok" role="status">
          <strong>✓ {message}</strong>
          {detail && <span className="deploy-hint">{detail}</span>}
        </div>
      )}

      {state === "error" && (
        <div className="deploy-popover deploy-result-error" role="alert">
          <span>{message}</span>
          <div className="deploy-actions">
            <button type="button" className="deploy-btn deploy-btn-primary" onClick={() => void run()}>Try again</button>
            <button type="button" className="deploy-cancel" onClick={() => setState("idle")}>Dismiss</button>
          </div>
        </div>
      )}
    </div>
  );
}
